/** Display helpers for the clip creator. Pure, no React. */

import { formatDuration } from '@/lib/format'

/** ``m:ss``, guarded against the NaN/Infinity a video element reports
 * before its metadata loads. */
export function fmt(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00'
  return formatDuration(seconds)
}

/**
 * Same clock, but with decimals once the timeline is zoomed in far
 * enough that whole seconds would make consecutive ticks read the same.
 * ``span`` is the visible window in seconds.
 */
export function fmtPrecise(seconds: number, span: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00'
  if (span > 60) return fmt(seconds)
  const decimals = span > 10 ? 1 : 2
  const whole = Math.floor(seconds)
  const frac = (seconds - whole).toFixed(decimals).slice(1) // ".25"
  return `${formatDuration(whole)}${frac}`
}

export function formatBytes(bytes: number | null | undefined): string | null {
  if (!bytes || bytes < 0) return null
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/**
 * "~2 min" for the remaining time of an upload. Coarse on purpose: the
 * estimate comes from an average rate that swings a lot early on, and a
 * number ticking second by second reads as more precise than it is.
 * Returns ``null`` when there's nothing meaningful to show.
 */
export function etaLabel(seconds: number): string | null {
  if (!isFinite(seconds) || seconds <= 0) return null
  if (seconds < 90) return `~${String(Math.max(5, Math.round(seconds / 5) * 5))} s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `~${String(minutes)} min`
  return `~${String(Math.round(minutes / 60))} h`
}

/**
 * Compact "há X" pt-BR relative time — good enough for a list of
 * projects, no i18n lib needed.
 */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const diffSec = Math.max(0, (Date.now() - then) / 1000)
  if (diffSec < 60) return 'agora'
  if (diffSec < 3600) return `há ${String(Math.floor(diffSec / 60))} min`
  if (diffSec < 86_400) return `há ${String(Math.floor(diffSec / 3600))} h`
  const days = Math.floor(diffSec / 86_400)
  if (days < 30) return `há ${String(days)} ${days === 1 ? 'dia' : 'dias'}`
  const months = Math.floor(days / 30)
  return `há ${String(months)} ${months === 1 ? 'mês' : 'meses'}`
}

/**
 * A clip's rate, the way an editor writes it: "2×", "0,5×". Trailing zeros
 * dropped and a comma for the decimal, since every other number this tool
 * shows is pt-BR.
 */
export function formatSpeed(speed: number): string {
  const rounded = Math.round(speed * 100) / 100
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
  return `${text}×`
}

/** "1 clipe" / "3 clipes" — the count tag in the panel header. */
export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `1 ${singular}` : `${String(count)} ${plural}`
}
